import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { FiChevronDown, FiChevronRight, FiExternalLink, FiLogOut, FiSettings } from 'react-icons/fi'
import { ContextSwitcher } from './ContextSwitcher'
import { UserSwitcher } from './UserSwitcher'
import { ThemeSwitcher } from '../common'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'
import { useNavigationItems } from './navigation/useNavigationItems'
import type { NavItemData, NavSection } from './navigation/useNavigationItems'

function SidebarNavItem({ to, icon: Icon, label, external, badgeCount }: NavItemData) {
  const location = useLocation()
  const isActive = !external && (location.pathname === to || location.pathname.startsWith(to + '/'))

  const itemClasses = cn(
    'flex items-center gap-3 px-4 cursor-pointer text-sidebar-text transition-all duration-200 ease-in-out hover:bg-sidebar-active',
    isActive ? 'bg-sidebar-active border-l-[length:var(--theme-nav-item-border-width,3px)] border-l-sidebar-active-border' : 'border-l-[length:var(--theme-nav-item-border-width,3px)] border-l-transparent'
  )

  const itemStyle = {
    borderRadius: 'var(--theme-nav-item-radius, 0)',
    paddingTop: 'var(--theme-space-md, 0.75rem)',
    paddingBottom: 'var(--theme-space-md, 0.75rem)',
    transition: `all var(--theme-transition, 0.2s) ease`,
  }

  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        <div className={itemClasses} style={itemStyle}>
          <Icon size={18} />
          <span className="text-sm font-normal">{label}</span>
          <FiExternalLink size={12} className="ml-auto opacity-50" />
        </div>
      </a>
    )
  }

  return (
    <NavLink to={to} style={{ textDecoration: 'none' }}>
      <div className={itemClasses} style={itemStyle}>
        <Icon size={18} />
        <span className={cn('text-sm', isActive ? 'font-semibold' : 'font-normal')}>
          {label}
        </span>
        {badgeCount != null && badgeCount > 0 && (
          <span className="ml-auto bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </div>
    </NavLink>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="px-4 text-xs font-bold text-sidebar-muted uppercase tracking-wide"
      style={{
        paddingTop: 'var(--theme-space-sm, 0.5rem)',
        paddingBottom: 'var(--theme-space-sm, 0.5rem)',
      }}
    >
      {children}
    </p>
  )
}

function CollapsibleSection({ title, isExpanded, onToggle, badgeCount, children }: {
  title: string; isExpanded: boolean; onToggle: () => void; badgeCount?: number; children: React.ReactNode
}) {
  return (
    <div>
      <button
        className="flex w-full items-center justify-between px-4 py-2 cursor-pointer text-sidebar-muted hover:bg-sidebar-active transition-all duration-200"
        onClick={onToggle}
      >
        <span className="text-xs font-bold uppercase tracking-wide flex items-center gap-2">
          {title}
          {badgeCount != null && badgeCount > 0 && (
            <span className="bg-accent text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
        </span>
        {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
      </button>
      {isExpanded && <div>{children}</div>}
    </div>
  )
}

function CollapsibleSubSection({ sub }: { sub: { title: string; icon: React.ComponentType<{ size?: number; className?: string }>; items: NavItemData[]; emptyMessage?: string } }) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div>
      <button
        className="flex w-full items-center justify-between px-4 py-1.5 cursor-pointer hover:bg-sidebar-active/50 transition-all duration-200"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <sub.icon size={14} className="text-sidebar-muted" />
          <span className="text-xs text-sidebar-muted font-medium">{sub.title}</span>
        </div>
        {isExpanded ? <FiChevronDown size={12} className="text-sidebar-muted" /> : <FiChevronRight size={12} className="text-sidebar-muted" />}
      </button>
      {isExpanded && (
        sub.items.length > 0 ? (
          sub.items.map((item) => <SidebarNavItem key={item.to} {...item} />)
        ) : sub.emptyMessage ? (
          <p className="px-4 py-2 text-xs text-sidebar-muted italic">{sub.emptyMessage}</p>
        ) : null
      )}
    </div>
  )
}

function SidebarSection({ section }: { section: NavSection }) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Collapsible with sub-sections (communities, etc.)
  if (section.isCollapsible && section.subSections) {
    return (
      <div className="mt-4">
        <CollapsibleSection
          title={section.title}
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
          badgeCount={section.badgeCount}
        >
          {section.items.length > 0 && (
            <div className="mb-1">
              {section.items.map((item) => <SidebarNavItem key={item.to} {...item} />)}
            </div>
          )}
          {section.subSections.map((sub) => (
            <CollapsibleSubSection key={sub.title} sub={sub} />
          ))}
          {section.overflowLink && <SidebarNavItem {...section.overflowLink} />}
        </CollapsibleSection>
      </div>
    )
  }

  // Collapsible with flat items (admin category headers, etc.)
  if (section.isCollapsible && section.items.length > 0) {
    return (
      <div className="mt-4">
        <CollapsibleSection
          title={section.title}
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
          badgeCount={section.badgeCount}
        >
          {section.items.map((item) => <SidebarNavItem key={item.to} {...item} />)}
        </CollapsibleSection>
      </div>
    )
  }

  // Non-collapsible flat section
  return (
    <div className={section.id === 'general' ? 'mt-2' : 'mt-4'}>
      <SectionHeader>{section.title}</SectionHeader>
      {section.items.map((item) => <SidebarNavItem key={item.to} {...item} />)}
    </div>
  )
}

interface SidebarProps {
  userName?: string
  userInitials?: string
  onLogout?: () => void
}

export function Sidebar({ userName = 'User', userInitials = 'U', onLogout }: SidebarProps) {
  const { sections, role, roleLabel, hasSchool } = useNavigationItems()

  return (
    <aside
      data-figma-component="Sidebar"
      className="bg-sidebar fixed flex flex-col"
      style={{
        width: 'var(--theme-sidebar-width, 200px)',
        left: 'var(--theme-sidebar-inset, 0px)',
        top: 'var(--theme-sidebar-inset, 0px)',
        bottom: 'var(--theme-sidebar-inset, 0px)',
        borderRadius: 'var(--theme-sidebar-radius, 0)',
      }}
    >
      {/* Logo */}
      <div className="flex justify-center px-4 py-4">
        <img src="/docurious-logo.png" alt="DOcurious" className="h-7 w-auto" />
      </div>

      {/* User Profile */}
      <div className="flex flex-col items-center py-4 gap-2">
        <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center">
          <span className="text-lg font-bold text-white">{userInitials}</span>
        </div>
        <span className="text-sm text-sidebar-text">Hi {userName}</span>
        {role && (
          <Badge variant="outline" size="sm" className="bg-blue-500/20 text-blue-200 border-blue-400/30 text-[9px]">
            {roleLabel}
          </Badge>
        )}
      </div>

      {/* Context Switcher — hidden for students (school is just a community for them) */}
      {hasSchool && role !== 'student' && (
        <div className="px-4">
          <ContextSwitcher />
        </div>
      )}

      {/* Scrollable navigation area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {sections.map((section) => (
          <SidebarSection key={section.id} section={section} />
        ))}
      </div>

      {/* Bottom controls */}
      <div className="pb-4 border-t border-sidebar-muted/20 pt-3">
        <div className="px-3 mb-2">
          <UserSwitcher />
        </div>
        <div className="flex px-4 py-2 text-sidebar-text">
          <ThemeSwitcher />
        </div>
        <NavLink
          to="/account"
          className="flex items-center gap-3 px-4 py-2.5 w-full text-sidebar-text hover:bg-sidebar-active transition-all duration-200"
          style={{ textDecoration: 'none' }}
        >
          <FiSettings size={18} />
          <span className="text-sm">Account Settings</span>
        </NavLink>
        <button
          className="flex items-center gap-3 px-4 py-2.5 w-full cursor-pointer text-sidebar-text hover:bg-sidebar-active transition-all duration-200"
          onClick={onLogout}
        >
          <FiLogOut size={18} />
          <span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  )
}
